import { useEffect, useMemo, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';

import { applyResultContentQuery } from '@/__generated__/applyResultContentQuery.graphql';
import { getCorporationRefundData } from '@/components/tax-refund/common/data/corporation-refund-data';
import { getIndisSearchRefundData } from '@/components/tax-refund/common/data/indis-search-refund-data';
import { applyResultContent } from '@/graphql/query/apply/apply-result';
import { usePaymentCard } from '@/lib/hooks/use-payment-card';
import { BusinessType, CorpNode, IndisNode, QueryUserData } from '@/lib/types/request';
import { formatAccount, formatDate, shortenBizName } from '@/lib/utils/stringFormat';
import { zCurrency } from '@/lib/utils/zCurrency';

export const useApplyResultData = (businessType: BusinessType) => {
  const data = useLazyLoadQuery<applyResultContentQuery>(applyResultContent, {}, { fetchPolicy: 'network-only' });
  const userData: QueryUserData = data?.bznavRefund?.user;
  const tritxRefundInfo = userData?.tritxIndis?.[0];
  const taxType = businessType === 'corps' ? 'income' : userData?.indis?.[0]?.refundStatus === 'ApplyComplete' ? 'income' : 'tritx';
  const { hasRegisteredCard, cardName, cardLogo, cardNumber, fetchCardData } = usePaymentCard();
  const isRefundCompleted = ['RefundCompleteA', 'RefundCompleteB', 'PaymentComplete', 'RefundFinish'].includes(tritxRefundInfo?.refundStage ?? '');
  const [disabledCancelButton, setDisabledCancelButton] = useState(isRefundCompleted);
  const [disabledRevokeButton, setDisabledRevokeButton] = useState(false);

  const reusultData = useMemo(() => {
    if (taxType === 'tritx') return null;

    if (businessType === 'corps') {
      const latestCorporationRefund = getCorporationRefundData();
      return userData?.corps?.find(({ refundId }) => refundId === latestCorporationRefund?.refundId);
    }
    const savedIndisRefund = getIndisSearchRefundData();
    const currentIndis = userData?.indis && userData.indis[0];
    const previousIndis = userData?.previousIndis?.find(({ refundId }) => refundId === savedIndisRefund?.refundId);
    return previousIndis || currentIndis;
  }, [businessType, taxType, userData?.corps, userData.indis, userData?.previousIndis]);

  const applyResultData = useMemo(() => {
    if (taxType === 'income') {
      if (!reusultData) return null;
      return {
        refundId: reusultData.refundId,
        name:
          businessType === 'corps'
            ? shortenBizName((reusultData as CorpNode)?.businessInfo?.hometaxBizName ?? '')
            : (reusultData as IndisNode)?.businessInfo?.taxpayerName || '고객',
        fullName: businessType === 'corps' ? ((reusultData as CorpNode)?.businessInfo?.hometaxBizName ?? '') : undefined,
        refundAmount: zCurrency(reusultData.refundAmount, '원', true, '0'),
        refundStatus: reusultData.refundStatus,
        refundStage: reusultData.refundStage,
        cancelStage: reusultData.cancelStage,
        applyDate: formatDate(reusultData.applicationDt),
        resultDate: formatDate(reusultData.refundResultDt),
        depositExpectDate: formatDate(reusultData.depositExpectDt),
        applicationInfo: { bank: reusultData.applicationInfo?.bank, accountNo: reusultData.applicationInfo?.accountNo },
        paymentAmount: zCurrency(reusultData.virtualAccountInfo?.paymentAmount, '원', false, ''),
        defaultFeeRate: reusultData.partnerInfo?.defaultFeeRate ?? '30',
        feeRate: reusultData.partnerInfo?.feeRate ?? '30',
        discountRate: reusultData.partnerInfo?.discountRate ?? '0',
        isPaymentGuide: reusultData.refundStage === 'RefundCompleteA',
        virtualAccountOwner: reusultData.virtualAccountInfo?.owner ?? '',
        virtualAccountNo: formatAccount(reusultData.virtualAccountInfo?.account),
        paymentDate: formatDate(reusultData.paymentDate),
        paymentCard: {
          hasRegisteredCard,
          cardName,
          cardLogo,
          cardNumber
        },
        businessType,
        taxType: 'income'
      };
    } else if (taxType === 'tritx') {
      return {
        tritxRefundInfo,
        disabledCancelButton,
        disabledRevokeButton,
        isRefundCompleted,
        paymentCard: {
          hasRegisteredCard,
          cardName,
          cardLogo,
          cardNumber
        },
        taxType: 'tritx'
      };
    }
  }, [
    businessType,
    cardLogo,
    cardName,
    cardNumber,
    disabledCancelButton,
    disabledRevokeButton,
    hasRegisteredCard,
    isRefundCompleted,
    taxType,
    tritxRefundInfo,
    userData
  ]);

  useEffect(() => {
    if (businessType === 'indis') {
      fetchCardData();
    }
  }, [businessType, fetchCardData]);

  return { reusultData, applyResultData, setDisabledCancelButton, setDisabledRevokeButton };
};
